// ── The bulk bar — always present, and never silent ───────────────────────────────────────────
//
// ⚠ ALWAYS VISIBLE, DISABLED AT ZERO. A bar that appears on selection teaches nobody it exists;
// a disabled bar with a zero-state tells a first-time user what selecting is FOR.
//
// ⚠ AND EVERY OUTCOME IS NAMED. The surface this replaces looped N single calls from the browser
// and threw the answers away — selection cleared, failures invisible. Once V1051 made an
// already-assigned control a 409, the controls a user most needed to hear about were exactly the
// silent ones. Nothing here reports a count without the references behind it.
//
// ⚠ TWO DIFFERENT INTERACTION SHAPES, BECAUSE THE TWO OPERATIONS DIFFER:
//   · Assignee is ADDITIVE and constrained — it cannot overwrite anything, but V1051 means some
//     controls legitimately refuse. So: act, then REPORT what happened.
//   · Responsible/Approver OVERWRITE — a column already holding someone is replaced silently
//     unless we say so first. So: CONFIRM, with the replacement count, before acting.

import { useMemo, useState } from 'react';
import { CTS_PEOPLE_LABEL, type CtsPeopleLane } from '@grc/shared';
import { cn } from '@/lib/utils';
import type { TeamMember } from '@/api/types/settings.types';
import type { WorkspaceControl } from '@/api/types/cts-workspace.types';
import { useBulkAssign, useBulkSetControlRole } from '@/api/hooks/useCtsWorkspace';
import { useBulkRemoveControls } from '@/api/hooks/useCtsTasks';
import { PeoplePicker } from './PeopleLanes';

type Lane = CtsPeopleLane;

interface AssignReport {
  kind: 'assignee';
  assigned: { reference: string; id: string }[];
  skipped: { reference: string; reason: string }[];
}
interface RemoveReport {
  kind: 'remove';
  removed: string[];
  skipped: { reference: string; reason: 'not_in_workspace' }[];
}
interface RoleReport {
  kind: 'role';
  lane: Exclude<Lane, 'assignee'>;
  set: { reference: string; previous: string | null }[];
  replaced: number;
}

const BTN = 'rounded-[7px] px-[11px] py-[6px] text-[12px] transition-colors disabled:opacity-40';

export function BulkPeopleBar({
  selected, rows, team, onClear,
}: {
  /** References of the VISIBLE, selected rows — never a filter-hidden one. */
  selected: string[];
  /** The visible rows, so a confirm can say how many already hold someone. */
  rows: WorkspaceControl[];
  team: TeamMember[] | undefined;
  onClear: () => void;
}) {
  const [lane, setLane] = useState<Lane | null>(null);
  const [pick, setPick] = useState('');
  const [report, setReport] = useState<AssignReport | RoleReport | RemoveReport | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const bulkAssign = useBulkAssign();
  const bulkRole = useBulkSetControlRole();
  // ⚠ P1.2 PD-D — "Remove from workspace" is ONE reported request, not a loop of silent ones. The
  //   page used to fire a request per row and clear the selection without reading a single answer.
  const bulkRemove = useBulkRemoveControls();
  const removing = bulkRemove.isPending;
  const runRemove = () => {
    setRemoveError(null);
    bulkRemove.mutate(selected, {
      onSuccess: (data) => { setReport({ kind: 'remove', removed: data.removed, skipped: data.skipped }); onClear(); },
      // A failed request clears NOTHING — the selection stays so the user can try again.
      onError: () => setRemoveError('The controls could not be removed. Nothing was changed.'),
    });
  };
  const busy = bulkAssign.isPending || bulkRole.isPending;
  const n = selected.length;

  /**
   * How many of the selection already hold this lane.
   *
   * ⚠ AN ESTIMATE FROM THE LOADED LIST, AND THE REPORT AFTERWARDS IS THE TRUTH. This is what the
   * user is shown BEFORE confirming, so it must exist; but it is computed from rows fetched
   * earlier, and the authoritative count comes back from the write itself.
   */
  const alreadyHeld = useMemo(() => {
    if (!lane || lane === 'assignee') return 0;
    const sel = new Set(selected);
    return rows.filter((r) => sel.has(r.control_reference)
      && (lane === 'responsible' ? r.owner_name : r.approver_name) !== null).length;
  }, [lane, selected, rows]);

  const close = () => { setLane(null); setPick(''); };

  const runAssign = () => bulkAssign.mutate(
    { controlReferences: selected, assigneeUserId: pick },
    {
      onSuccess: (data) => {
        setReport({ kind: 'assignee', assigned: data.assigned, skipped: data.skipped });
        close(); onClear();
      },
    });

  const runRole = () => {
    // Captured so the success callback carries the narrowed lane — `lane` is state and TypeScript
    // cannot know it has not changed by the time the promise settles. Nor could a reader.
    const l = lane;
    if (!l || l === 'assignee') return;
    bulkRole.mutate(
      { controlReferences: selected, role: l, userId: pick },
      {
        onSuccess: (data) => {
          setReport({ kind: 'role', lane: l, set: data.set, replaced: data.replaced });
          close(); onClear();
        },
      });
  };

  /**
   * ⚠ UNDO RESTORES EACH CONTROL'S OWN PREVIOUS HOLDER, which is why the write returns the previous
   * value per control rather than a count. Controls are grouped by who they had before, so a mixed
   * selection is restored in a few calls rather than one per row.
   */
  const undoRole = (r: RoleReport) => {
    const byPrevious = new Map<string | null, string[]>();
    for (const s of r.set) {
      byPrevious.set(s.previous, [...(byPrevious.get(s.previous) ?? []), s.reference]);
    }
    for (const [previous, refs] of byPrevious) {
      bulkRole.mutate({ controlReferences: refs, role: r.lane, userId: previous });
    }
    setReport(null);
  };

  return (
    <div className="mb-[10px] flex flex-col gap-[8px]">
      <div
        role="region" aria-label="Bulk actions"
        className={cn('flex flex-wrap items-center gap-[10px] rounded-[9px] border px-[14px] py-[10px]',
          n > 0 ? 'border-accent-cyan/35 bg-accent-cyan/8' : 'border-border-subtle bg-bg-surface-2')}
      >
        <span className={cn('text-[12.5px] font-medium',
          n > 0 ? 'text-text-primary' : 'text-text-faint')}>
          {n > 0
            ? `${n} ${n === 1 ? 'control' : 'controls'} selected`
            : 'Select controls to act on'}
        </span>
        <span className="flex flex-wrap items-center gap-[8px]">
          {/* ⚠ THE LANE IS CHOSEN BY THE BUTTON. There is no combined "assign all three" — three
              separate facts, three separate acts. */}
          <button type="button" disabled={n === 0 || busy} onClick={() => setLane('assignee')}
            className={cn(BTN, 'border border-accent-cyan/50 bg-bg-surface text-text-primary')}>
            Assign work
          </button>
          {(['responsible', 'accountable'] as const).map((l) => (
            <button key={l} type="button" disabled={n === 0 || busy} onClick={() => setLane(l)}
              className={cn(BTN, 'border border-border-subtle bg-bg-surface text-text-primary')}>
              Set {CTS_PEOPLE_LABEL[l]}
            </button>
          ))}
          <button type="button" disabled={n === 0 || busy || removing} onClick={runRemove}
            className={cn(BTN, 'border border-border-subtle bg-bg-surface text-text-primary')}>
            Remove from workspace
          </button>
          <button type="button" disabled title="Placeholder — transitions act on an assignment, not on a control selection"
            className={cn(BTN, 'border border-cert-neutral-border bg-bg-surface text-text-secondary opacity-60')}>
            Request approval
          </button>
        </span>
        {n > 0 && (
          <button type="button" onClick={onClear} aria-label="Clear selection"
            className="ml-auto text-[12px] text-text-secondary transition-colors hover:text-text-primary">
            Clear
          </button>
        )}
        {/* ⚠ THE REVERSIBILITY PROMISE LIVES INSIDE THE BAR, not in a tooltip and not beside it.
            "Remove" reads as deletion to anyone who has not been told otherwise, and this line is
            the telling — it was nearly lost in the restyle, which is exactly how a
            destructive-sounding action becomes frightening without anyone changing what it does. */}
        <p className="w-full text-[11.5px] text-text-faint">
          Removing deactivates — history, evidence &amp; assessments are kept.
        </p>
      </div>

      {/* ── the lane modal ── */}
      {lane && (
        <div className="rounded-[9px] border border-accent-cyan/35 bg-bg-surface-2 p-[14px]">
          <p className="mb-[8px] text-[13px] font-medium text-text-primary">
            {lane === 'assignee'
              ? `Assign work on ${n} ${n === 1 ? 'control' : 'controls'}`
              : `Set ${CTS_PEOPLE_LABEL[lane]} on ${n} ${n === 1 ? 'control' : 'controls'}`}
          </p>
          {lane !== 'assignee' && (
            // ⚠ CONFIRM-FIRST, BECAUSE THIS OVERWRITES. Replacing three named people silently is
            //    the failure mode; saying so before the click is the whole point of this line.
            <p className="mb-[8px] text-[12px] text-cert-warn-fg">
              {alreadyHeld > 0
                ? `${alreadyHeld} of these already ${alreadyHeld === 1 ? 'has' : 'have'} a ${CTS_PEOPLE_LABEL[lane]} — they will be replaced.`
                : `None of these currently has a ${CTS_PEOPLE_LABEL[lane]}.`}
            </p>
          )}
          <div className="flex flex-col gap-[8px]">
            <PeoplePicker id="bulk-person" lane={lane} team={team} value={pick} onChange={setPick} />
            <div className="flex gap-[8px]">
              <button
                type="button" disabled={busy || !pick}
                onClick={() => (lane === 'assignee' ? runAssign() : runRole())}
                className={cn(BTN, 'bg-accent-cyan/15 text-accent-cyan')}
              >
                {lane === 'assignee' ? 'Assign' : `Set ${CTS_PEOPLE_LABEL[lane]}`}
              </button>
              <button type="button" onClick={close} disabled={busy}
                className={cn(BTN, 'text-text-secondary')}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── the report ── */}
      {report?.kind === 'assignee' && (
        <div className="rounded-[9px] border border-border-subtle bg-bg-surface-2 p-[14px]">
          <p className="text-[13px] text-text-primary">
            Assigned {report.assigned.length}
            {report.skipped.length > 0 && <> · Skipped {report.skipped.length}</>}
          </p>
          {report.skipped.length > 0 && (
            // ⚠ NAMED, NOT COUNTED. "Skipped 4" tells a user nothing they can act on; these are
            //    the controls they now have to look at, so they are listed.
            <ul className="mt-[6px] space-y-[3px]">
              {report.skipped.map((s) => (
                <li key={s.reference} className="text-[12px] text-cert-warn-fg">
                  {s.reference} — {s.reason === 'already_assigned'
                    ? 'already has open work' : 'no live control'}
                </li>
              ))}
            </ul>
          )}
          {/* ⓘ No undo here, and that is a product fact rather than an omission: `unassigned` is
              the ABSENCE of an assignment row, assignment rows are never deleted, and no transition
              leads back to it. Undoing a bulk assign would need a capability the machine does not
              have. Saying nothing would imply one exists. */}
          <p className="mt-[8px] text-[11.5px] text-text-faint">
            Assignments cannot be undone in bulk — an assignment is withdrawn by moving it through
            its own lifecycle on the control.
          </p>
          <button type="button" onClick={() => setReport(null)}
            className={cn(BTN, 'mt-[8px] text-text-secondary')}>Dismiss</button>
        </div>
      )}

      {removeError && (
        <div role="alert" className="rounded-[9px] border border-cert-warn-border bg-bg-surface-2 p-[12px] text-[12.5px] text-cert-warn-fg">
          {removeError}
        </div>
      )}

      {report?.kind === 'remove' && (
        <div className="rounded-[9px] border border-border-subtle bg-bg-surface-2 p-[14px]">
          <p className="text-[13px] text-text-primary">
            Removed {report.removed.length}
            {report.skipped.length > 0 && <> · Skipped {report.skipped.length}</>}
          </p>
          {report.skipped.length > 0 && (
            // ⚠ NAMED, NOT COUNTED — and one reason, on purpose: the server does not distinguish
            //    "already removed" from "never here", so neither does the report.
            <ul className="mt-[6px] space-y-[3px]">
              {report.skipped.map((s) => (
                <li key={s.reference} className="text-[12px] text-cert-warn-fg">
                  {/* The code the user sees on the row, not the internal reference. */}
                  {rows.find((r) => r.control_reference === s.reference)?.display_code ?? s.reference} — not in this
                  workspace (already removed, or never added)
                </li>
              ))}
            </ul>
          )}
          <p className="mt-[8px] text-[11.5px] text-text-faint">
            Removing deactivates — history, evidence &amp; assessments are kept. A removed control can be added back.
          </p>
          <button type="button" onClick={() => setReport(null)}
            className={cn(BTN, 'mt-[8px] text-text-secondary')}>Dismiss</button>
        </div>
      )}

      {report?.kind === 'role' && (
        <div className="rounded-[9px] border border-border-subtle bg-bg-surface-2 p-[14px]">
          <p className="text-[13px] text-text-primary">
            {CTS_PEOPLE_LABEL[report.lane]} set on {report.set.length}
            {report.replaced > 0 && <> · replaced {report.replaced} existing</>}
          </p>
          <div className="mt-[8px] flex gap-[8px]">
            <button type="button" disabled={busy} onClick={() => undoRole(report)}
              className={cn(BTN, 'border border-border-subtle bg-bg-surface text-text-primary')}>
              Undo
            </button>
            <button type="button" onClick={() => setReport(null)}
              className={cn(BTN, 'text-text-secondary')}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
}
